import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PLATFORM_OPTIONS } from '../../lib/platforms';
import type { PlatformName } from '../../types/api';
import { GENERATION_PATH, type GenerationPrefill } from '../generation/generationPrefill';

/** The brief's topic cap; the prefill guard trims to the same number. */
const TOPIC_MAX_LENGTH = 500;

/** The tallest the field grows before it scrolls, in px. */
const MAX_FIELD_HEIGHT = 160;

/** The brand's own platforms first, in its order, then the rest. */
function orderedPlatforms(brandPlatforms: readonly string[]) {
  const mine = brandPlatforms.flatMap((value) => {
    const option = PLATFORM_OPTIONS.find((candidate) => candidate.value === value);
    return option ? [option] : [];
  });
  const rest = PLATFORM_OPTIONS.filter((option) => !mine.includes(option));
  return [...mine, ...rest];
}

/**
 * Home's way in: a topic and, optionally, a platform, handed to the brief at
 * `/generation` as a `GenerationPrefill` (read back by `readGenerationPrefill`).
 * Nothing is spent here — the quota goes when the brief is submitted, and the
 * whole brief sits in between.
 */
export function QuickStart({ brandPlatforms }: { brandPlatforms: readonly string[] }) {
  const navigate = useNavigate();
  const fieldId = useId();
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState<PlatformName | null>(null);

  const canGo = topic.trim().length > 0;

  function go(event?: { preventDefault: () => void }) {
    event?.preventDefault();
    if (!canGo) return;
    const prefill: GenerationPrefill = { topic: topic.trim() };
    if (platform) prefill.platform = platform;
    void navigate(GENERATION_PATH, { state: prefill });
  }

  // One line that grows with what is typed, up to a cap.
  function grow() {
    const field = fieldRef.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, MAX_FIELD_HEIGHT)}px`;
  }

  // Enter goes, Shift+Enter is a new line — the way a one-line box reads.
  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) go(event);
  }

  return (
    <form className="quick-start" onSubmit={go}>
      <label className="quick-start__label" htmlFor={fieldId}>
        What do you want to post about?
      </label>
      <div className="quick-start__row">
        <textarea
          ref={fieldRef}
          id={fieldId}
          className="quick-start__field"
          rows={1}
          maxLength={TOPIC_MAX_LENGTH}
          value={topic}
          placeholder="A launch, an idea, a lesson from this week"
          onChange={(event) => {
            setTopic(event.target.value);
            grow();
          }}
          onKeyDown={onKeyDown}
        />
        <button className="button button--primary" type="submit" disabled={!canGo}>
          Go
        </button>
      </div>
      <div className="quick-start__foot">
        <div className="quick-start__platforms" role="group" aria-label="Platform (optional)">
          {orderedPlatforms(brandPlatforms).map((option) => (
            <button
              key={option.value}
              className="quick-start__chip"
              type="button"
              aria-pressed={platform === option.value}
              onClick={() =>
                setPlatform((current) => (current === option.value ? null : option.value))
              }
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link className="quick-start__template" to="/templates">
          Start from a template
        </Link>
      </div>
    </form>
  );
}
